export const STUDY_MATERIAL = {
  anatomy: {
    title: 'Ocular Anatomy & Physiology',
    sections: [
      {
        title: 'The Eye — External Structures',
        content: `The eye is protected and supported by several external structures:

• **Orbit** — The bony socket that houses the eye, made up of seven bones. It protects the globe from trauma.

• **Eyelids (Palpebrae)** — Protect the eye from debris and distribute tears across the cornea with each blink. The upper lid is more mobile and covers more of the cornea.

• **Conjunctiva** — A thin, transparent mucous membrane that lines the inner surface of the eyelids (palpebral conjunctiva) and covers the anterior sclera (bulbar conjunctiva). It produces mucus to help lubricate the eye.

• **Lacrimal System** — Produces, distributes, and drains tears. The lacrimal gland (located superiorly and laterally) secretes tears that wash across the eye and drain through the puncta into the nasolacrimal duct.

• **Extraocular Muscles** — Six muscles control eye movement:
  - Medial Rectus → moves eye inward (adduction)
  - Lateral Rectus → moves eye outward (abduction)
  - Superior Rectus → moves eye upward
  - Inferior Rectus → moves eye downward
  - Superior Oblique → rotates eye inward and downward
  - Inferior Oblique → rotates eye outward and upward`,
      },
      {
        title: 'The Globe — Layers of the Eye',
        content: `The eyeball (globe) has three main layers:

**1. Fibrous Tunic (Outer Layer)**
• **Sclera** — The "white of the eye," a tough, opaque tissue that maintains the shape of the eye and protects internal structures. It is continuous with the cornea anteriorly.
• **Cornea** — The transparent, avascular front surface of the eye. It provides approximately **two-thirds (about 43 diopters)** of the eye's total refractive power. It has five layers: epithelium, Bowman's membrane, stroma, Descemet's membrane, and endothelium.

**2. Vascular Tunic (Middle Layer / Uvea)**
• **Iris** — The colored part of the eye that controls the size of the pupil and regulates the amount of light entering the eye.
• **Ciliary Body** — Produces aqueous humor and contains the ciliary muscle, which controls accommodation (focusing at different distances) by changing the shape of the crystalline lens.
• **Choroid** — A highly vascular layer that supplies nutrients and oxygen to the retina. It lies between the sclera and retina.

**3. Neural Tunic (Inner Layer)**
• **Retina** — The light-sensitive layer containing photoreceptors:
  - **Rods** (~120 million) — Responsible for dim-light (scotopic) and peripheral vision. They do not detect color.
  - **Cones** (~6 million) — Responsible for bright-light (photopic) vision, color perception, and fine detail. Concentrated in the **macula**.
• **Macula Lutea** — The central area of the retina responsible for sharp, central vision.
• **Fovea Centralis** — The center of the macula with the highest concentration of cones. Provides the sharpest visual acuity.
• **Optic Disc** — Where the optic nerve exits the eye. It has no photoreceptors, creating the **blind spot**.`,
      },
      {
        title: 'Internal Structures & Refractive Media',
        content: `**Aqueous Humor**
• A clear fluid produced by the ciliary body that fills the anterior and posterior chambers (between the cornea and the lens).
• Provides nutrients to the cornea and lens, and maintains intraocular pressure (IOP).
• Drains through the **trabecular meshwork** into **Schlemm's canal**. Blocked drainage can lead to glaucoma.

**Crystalline Lens**
• A biconvex, transparent structure located behind the iris.
• Provides approximately **one-third** of the eye's total refractive power (about 15-20 diopters).
• Changes shape during **accommodation** — becomes more convex for near vision, flatter for distance vision.
• Loss of lens flexibility with age causes **presbyopia** (typically begins around age 40).
• Cloudiness of the lens is called a **cataract**.

**Vitreous Humor**
• A clear, gel-like substance filling the space between the lens and the retina (vitreous chamber).
• Helps maintain the shape of the eye and holds the retina in place.

**Refractive Path of Light:**
Cornea → Aqueous Humor → Pupil → Crystalline Lens → Vitreous Humor → Retina`,
      },
      {
        title: 'Common Refractive Errors',
        content: `**Emmetropia** — Normal vision; light focuses directly on the retina without correction.

**Myopia (Nearsightedness)**
• The eye is too long or the cornea too steep, causing light to focus in front of the retina.
• Corrected with **minus (concave/diverging)** lenses.
• Distant objects appear blurry; near vision is relatively clear.

**Hyperopia (Farsightedness)**
• The eye is too short or the cornea too flat, causing light to focus behind the retina.
• Corrected with **plus (convex/converging)** lenses.
• Near objects are more blurry; some distance blur may also occur.

**Astigmatism**
• The cornea or lens has an irregular shape (like a football rather than a basketball), causing light to focus at multiple points.
• Corrected with **cylindrical** lenses that have power in one specific meridian.
• The Rx includes a cylinder power and axis.

**Presbyopia**
• Age-related loss of accommodation due to hardening of the crystalline lens.
• Corrected with **plus power for near** (reading glasses, bifocals, progressives).
• Typically becomes noticeable around age **40**.

**Aphakia**
• Absence of the crystalline lens (usually after cataract surgery without an IOL implant).
• Requires high-plus correction (approximately +10.00 to +14.00 D).`,
      },
    ],
  },

  lenses: {
    title: 'Ophthalmic Lenses',
    sections: [
      {
        title: 'Lens Types and Forms',
        content: `**Single Vision Lenses**
• Have one prescription power throughout the entire lens.
• Used to correct myopia, hyperopia, or astigmatism.

**Multifocal Lenses**

• **Bifocals** — Two distinct viewing zones: distance on top, near (segment/seg) on bottom.
  - Common styles: Flat-Top (D-seg/straight-top) 28mm, Flat-Top 35mm, Round seg, Executive (Franklin)
  - The **Executive** (Franklin) bifocal has a seg that extends the full width of the lens.
  - The seg line is the dividing line between distance and near.

• **Trifocals** — Three zones: distance (top), intermediate (middle), near (bottom).
  - The intermediate zone is typically half the add power.

• **Progressive Addition Lenses (PALs)** — No visible lines; a smooth, gradual transition from distance through intermediate to near.
  - Advantages: cosmetically appealing, no image jump, continuous vision at all distances.
  - Disadvantages: peripheral distortion/swim, adaptation period required, narrower intermediate and near zones.
  - The fitting cross is used for alignment and is positioned at the center of the pupil.

**Lens Forms (Meniscus)**
• Most ophthalmic lenses are **meniscus** shaped — convex on the front (base curve) and concave on the back.
• **Plus lenses** — Thicker in the center, thinner at the edges.
• **Minus lenses** — Thinner in the center, thicker at the edges.
• **Plano lenses** — No refractive power.`,
      },
      {
        title: 'Lens Materials',
        content: `**CR-39 (Columbia Resin #39)**
• Index of refraction: **1.50**
• The standard plastic lens material.
• Lightweight, good optical clarity, accepts tints well.
• Lower impact resistance than polycarbonate or Trivex.

**Polycarbonate**
• Index of refraction: **1.586**
• High impact resistance — required by FDA for children and safety eyewear.
• Thinner and lighter than CR-39.
• Built-in UV protection.
• Lower Abbe value (30) — more chromatic aberration.
• Requires scratch-resistant coating (softer material).

**Trivex**
• Index of refraction: **1.53**
• Excellent impact resistance (comparable to polycarbonate).
• Superior optical clarity — higher Abbe value (45) than polycarbonate.
• Lightest lens material available.
• Built-in UV protection.

**High-Index Plastics (1.60, 1.67, 1.74)**
• Thinner and lighter for strong prescriptions.
• Higher index = thinner lens but lower Abbe value (more chromatic aberration).
• 1.74 is the thinnest plastic lens material currently available.
• Recommended for prescriptions above ±4.00 D.

**Crown Glass**
• Index of refraction: **1.523**
• Excellent optical clarity, scratch-resistant.
• Heavy and prone to shattering — rarely used today.
• Abbe value: 59 (best optical quality).

**Abbe Value** — A measure of chromatic aberration (color fringing). Higher Abbe = less distortion.
| Material      | Index | Abbe Value |
|---------------|-------|-----------|
| CR-39         | 1.50  | 58        |
| Crown Glass   | 1.523 | 59        |
| Trivex        | 1.53  | 45        |
| Polycarbonate | 1.586 | 30        |
| Hi-Index 1.60 | 1.60  | 36        |
| Hi-Index 1.67 | 1.67  | 32        |
| Hi-Index 1.74 | 1.74  | 33        |`,
      },
      {
        title: 'Lens Treatments and Coatings',
        content: `**Anti-Reflective (AR) Coating**
• Reduces reflections from the lens surfaces, allowing more light to pass through.
• Improves visual clarity, especially for night driving and computer use.
• Reduces glare and ghosting; makes lenses appear nearly invisible.
• Applied to both front and back surfaces for best results.

**Scratch-Resistant Coating**
• A hard coating applied to protect the lens surface.
• Required for polycarbonate and high-index lenses (softer materials).
• Does not make lenses scratch-proof, only scratch-resistant.

**UV Protection**
• Blocks harmful ultraviolet radiation (UV-A and UV-B).
• Polycarbonate and Trivex have built-in 100% UV protection.
• CR-39 and other materials require a UV coating to be added.
• UV exposure can contribute to cataracts, macular degeneration, and pterygium.

**Photochromic Lenses**
• Darken automatically in UV light (outdoors) and return to clear indoors.
• Do not fully darken inside a car (windshields block UV).
• Newer generations activate and fade faster than older versions.
• Example brand: Transitions.

**Polarized Lenses**
• Eliminate reflected glare from flat surfaces (water, roads, snow).
• Contain a filter that blocks horizontally oriented light.
• Excellent for driving, fishing, and outdoor activities.
• May interfere with viewing LCD screens.

**Blue Light Filtering**
• Reduces exposure to high-energy visible (HEV) blue light from digital screens.
• May help reduce digital eye strain.`,
      },
      {
        title: 'Lens Surfacing and Optical Concepts',
        content: `**Base Curve**
• The front (convex) curve of the lens.
• Determined by the prescription and lens design.
• A flatter base curve = lower number; a steeper base curve = higher number.
• Choosing the correct base curve minimizes peripheral aberrations.

**Nominal Lens Formula (Approximate):**
Front Surface Power + Back Surface Power = Total Power
Example: +6.00 base curve + (-8.00) = -2.00 D lens

**True Power (Compensated):**
Uses the lensmaker's equation which accounts for lens thickness.

**Focal Length:**
f (in meters) = 1 / D (diopter power)
• A +2.00 D lens has a focal length of 0.50 m (50 cm).
• A -4.00 D lens has a focal length of -0.25 m (-25 cm).

**Transposition** — Converting between plus cylinder and minus cylinder forms:
1. Add the sphere and cylinder together to get the new sphere.
2. Change the sign of the cylinder.
3. Change the axis by 90 degrees.

Example: +2.00 -1.00 x090 → +1.00 +1.00 x180

**Optical Center** — The point on the lens where light passes through without deviation (no prismatic effect). The optical center should align with the patient's pupil center.

**Vertex Distance** — The distance between the back surface of the lens and the front of the cornea (typically 12-14mm). Vertex distance compensation is needed for prescriptions over ±4.00 D.`,
      },
    ],
  },

  frames: {
    title: 'Frames & Materials',
    sections: [
      {
        title: 'Frame Measurements (Boxing System)',
        content: `The **Boxing System** is the standard method for measuring frames used in opticianry.

**Key Measurements:**
• **A (Eye Size)** — The horizontal width of the lens opening (widest point of the box).
• **B (Vertical Size)** — The vertical height of the lens opening (tallest point of the box).
• **DBL (Bridge Size)** — The distance between the two lenses at the narrowest point of the bridge.
• **ED (Effective Diameter)** — The longest diagonal measurement of the lens opening. Used to determine minimum blank size.
• **Temple Length** — The length of the temple piece, measured from the hinge to the end of the temple tip.

**Frame PD = A + DBL**
This is also called the **Geometric Center Distance (GCD)**.

**Minimum Blank Size (MBS):**
MBS = ED + 2(decentration)
Decentration = (Frame PD - Patient PD) / 2

This tells you the smallest lens blank that will fill the frame after the optical center has been positioned correctly.

**Datum Line** — The horizontal line that divides the lens opening exactly in half vertically. This is also called the 180-degree line.`,
      },
      {
        title: 'Frame Types and Styles',
        content: `**Full-Rim Frames**
• The frame completely surrounds the lens.
• Most durable and secure; holds lenses firmly in place.
• Available in the widest variety of materials and styles.

**Semi-Rimless (Nylon Cord/Groove)**
• Frame rim covers only the top of the lens; bottom is held by a nylon cord fitted into a groove cut into the lens edge.
• Lightweight and less visible than full-rim frames.
• The lens must have a groove (bevel) on the lower edge.

**Rimless (Drill-Mount)**
• No frame around the lenses; lenses are attached directly to the bridge and temples via drilled holes and screws/bushings.
• Most lightweight and least visible.
• Lenses must be impact-resistant material (polycarbonate or Trivex recommended).
• More fragile and prone to loosening.

**Frame Categories by Material:**

**Metal Frames**
• Monel — Base metal alloy, affordable, may cause allergic reactions (contains nickel).
• Titanium — Lightweight, hypoallergenic, corrosion-resistant, strong. Premium material.
• Beta Titanium — More flexible than pure titanium.
• Stainless Steel — Affordable, lightweight, hypoallergenic.
• Beryllium — Resistant to corrosion, good for high-acid-content skin.
• Memory Metal (Flexon) — Returns to original shape when bent.

**Plastic Frames**
• Zyl (Zylonite/Cellulose Acetate) — Most common plastic frame material. Easy to adjust with heat, available in many colors.
• Propionate (Nylon-based) — Lightweight, hypoallergenic. Harder to adjust than zyl.
• Optyl — Epoxy resin; lightweight, holds adjustments well, memory plastic.
• TR-90 (Grilamid) — Very flexible and lightweight; popular for sports frames.
• Carbon Fiber — Extremely strong and lightweight.`,
      },
      {
        title: 'Frame Adjustments and Fitting',
        content: `**Standard Fitting Alignment:**
• Frame should sit level on the face.
• Temples should touch the ear at the mastoid bone and curve down gently.
• Nosepads (or bridge) should distribute weight evenly on both sides of the nose.
• Lenses should be close to the eyes (vertex distance ~12-14mm) but not touching eyelashes.

**Common Adjustments:**

• **Pantoscopic Tilt** — The frame tilts so the bottom of the lens is closer to the face than the top. Standard tilt is about **8-12 degrees**. This helps align the lens with the natural downward reading gaze.

• **Retroscopic Tilt** — The opposite of pantoscopic tilt; the top of the lens is closer to the face. Rarely desired.

• **Face Form (Wrap)** — The curve of the frame front as seen from above. Slight face form is normal; ensures the frame follows the curvature of the face.

• **Temple Spread** — Adjusting temples in or out for comfortable fit against the sides of the head.

• **Vertex Distance** — Adjusting the distance between the lens and the eye. Critical for high prescriptions.

**Adjustment Tools and Techniques:**
• Heat plastic frames before adjusting to avoid breakage (frame warmer at about 250-275°F / 120-135°C).
• Never apply heat directly to lenses.
• Use pliers with nylon jaws or protective sleeves to avoid scratching.
• Metal frames can be adjusted without heat.`,
      },
    ],
  },

  rx: {
    title: 'Prescription (Rx) Interpretation',
    sections: [
      {
        title: 'Reading an Ophthalmic Prescription',
        content: `**Standard Rx Format:**
OD (Right Eye): Sphere / Cylinder x Axis
OS (Left Eye): Sphere / Cylinder x Axis
Add: (for reading/near vision)

**Example:**
OD: -2.50 -0.75 x 180
OS: -3.00 -1.25 x 010
Add: +2.00

**Components:**

• **Sphere (Sph)** — Corrects myopia (-) or hyperopia (+). Measured in diopters (D).
  - Minus (-) = nearsighted correction (concave/diverging lens)
  - Plus (+) = farsighted correction (convex/converging lens)

• **Cylinder (Cyl)** — Corrects astigmatism. Always accompanied by an axis.
  - Can be written in minus cylinder or plus cylinder form.
  - Ophthalmologists typically write in plus cylinder; optometrists in minus cylinder.

• **Axis** — The meridian (in degrees from 1-180) at which zero cylinder power is placed.
  - Axis 90 = vertical; Axis 180 = horizontal.
  - The axis defines the ORIENTATION of the correction, not the location of the problem.

• **Add Power** — Additional plus power for near vision (presbyopia correction).
  - Always a positive number.
  - Typically ranges from +0.75 to +3.00 D.
  - The same add power applies to both eyes in most prescriptions.

**OD vs OS:**
• OD = Oculus Dexter = Right Eye
• OS = Oculus Sinister = Left Eye
• OU = Oculus Uterque = Both Eyes`,
      },
      {
        title: 'Transposition and Rx Conversion',
        content: `**Flat Transposition (Plus to Minus Cylinder or Vice Versa):**
Used to convert between plus and minus cylinder forms.

**Steps:**
1. Algebraically add the sphere and cylinder to get the new sphere.
2. Change the sign of the cylinder (keep the same magnitude).
3. Change the axis by 90° (add or subtract 90; keep between 1-180).

**Example 1:** Convert +1.00 -0.50 x090 to plus cylinder
1. +1.00 + (-0.50) = +0.50 (new sphere)
2. -0.50 → +0.50 (change sign)
3. 090 + 90 = 180 (new axis)
**Answer:** +0.50 +0.50 x180

**Example 2:** Convert -3.25 +1.75 x045 to minus cylinder
1. -3.25 + 1.75 = -1.50 (new sphere)
2. +1.75 → -1.75 (change sign)
3. 045 + 90 = 135 (new axis)
**Answer:** -1.50 -1.75 x135

**Spherical Equivalent:**
Sphere + (Cylinder / 2)
Used for contact lens fitting or quick estimation.

Example: -3.00 -1.00 x180
Spherical Equivalent = -3.00 + (-1.00/2) = -3.00 + (-0.50) = **-3.50**

**Vertex Distance Compensation:**
For Rx over ±4.00 D, the effective power changes as the lens moves closer to or further from the eye.

Compensated Power = D / (1 - d × D)
Where: D = original lens power, d = change in vertex distance in METERS.`,
      },
      {
        title: 'Special Prescriptions and Notations',
        content: `**Prism in the Rx:**
• Written as prism amount (in prism diopters Δ) and base direction.
• Base directions: BU (Base Up), BD (Base Down), BI (Base In), BO (Base Out).
• Example: 2Δ BO OD — 2 prism diopters, base out, right eye.

**Slab-Off (Bicentric Grinding):**
• Used to correct vertical imbalance in multifocal lenses when anisometropia (unequal Rx) exists.
• Applied to the lens with the LEAST minus (or MOST plus) power.
• Addresses the differential prismatic effect at the reading level.

**Compound Lenses:**
• A lens that has both spherical and cylindrical power.
• Example: -2.00 -1.50 x180 is a compound myopic astigmatic correction.

**Power Cross:**
• A diagram used to show the power in each meridian of a lens with cylinder.
• For -2.00 -1.00 x180:
  - At 180°: -2.00 (sphere only)
  - At 090°: -3.00 (sphere + cylinder)

**Determining the Type of Prescription:**
• Both meridians minus → Compound Myopic Astigmatism
• Both meridians plus → Compound Hyperopic Astigmatism
• One meridian plano, other minus → Simple Myopic Astigmatism
• One meridian plano, other plus → Simple Hyperopic Astigmatism
• One meridian plus, other minus → Mixed Astigmatism`,
      },
    ],
  },

  prism: {
    title: 'Prism',
    sections: [
      {
        title: 'Prism Basics',
        content: `**What is Prism?**
Prism bends (deviates) light toward its **base**. The image appears to shift toward the **apex** (the thinnest edge).

**Prism Diopter (Δ)**
One prism diopter deviates light **1 cm at a distance of 1 meter**.

**Uses of Prism in Eyewear:**
• Corrects eye alignment problems (strabismus, phoria)
• Relieves symptoms of binocular vision dysfunction (double vision, eyestrain, headaches)
• Can be ground into the lens (ground-in prism) or applied temporarily (Fresnel press-on prism)

**Base Directions:**
• **BI (Base In)** — The thick edge (base) is toward the nose. Used for exo deviations (eye turns outward).
• **BO (Base Out)** — The thick edge (base) is toward the temples. Used for eso deviations (eye turns inward).
• **BU (Base Up)** — Base is at the top. Used for hyper deviations of the opposite eye or hypo deviations of the same eye.
• **BD (Base Down)** — Base is at the bottom.

**Fresnel Prism:**
• Thin, flexible plastic press-on prism applied to the back surface of the lens.
• Used for temporary or diagnostic prism.
• Reduces visual acuity slightly due to the grooved surface.

**Prism Thinning:**
• Adding equal base-down prism to both lenses (usually in plus prescriptions) to reduce the thick top edge.
• Does not change the prismatic effect because it's equal in both eyes.`,
      },
      {
        title: "Prentice's Rule and Prismatic Effect",
        content: `**Prentice's Rule:**
Δ = c × D

Where:
• Δ = prismatic effect in prism diopters
• c = decentration in CENTIMETERS from the optical center
• D = lens power in diopters (use absolute value)

**Example 1:** A -4.00 D lens, looking 5mm below the optical center.
Δ = 0.5 cm × 4.00 = **2.0Δ**
Direction: For a minus lens, looking below the OC → Base UP (prism is toward the thinner edge, which is the center for minus lenses).

**Example 2:** A +3.00 D lens, decentered 8mm inward.
Δ = 0.8 cm × 3.00 = **2.4Δ Base In**

**Rules for Prism Direction:**
• **Plus lenses:** Prism base is in the SAME direction as decentration.
  - Decentered in → Base In. Decentered down → Base Down.
• **Minus lenses:** Prism base is in the OPPOSITE direction as decentration.
  - Decentered in → Base Out. Decentered down → Base Up.

**Memory aid:** Think of plus lenses as magnifying glasses — they're thick in the middle, so decentering them moves the thick part (base) in the same direction.

**Compounding and Resolving Prism:**
When prism exists in both horizontal and vertical directions, use the resultant:
Resultant = √(Horizontal² + Vertical²)
Direction (angle) = arctan(Vertical / Horizontal)`,
      },
      {
        title: 'Prescribed Prism and Splitting',
        content: `**Prescribed Prism:**
Prism may be prescribed to help align the eyes. It is specified as a diopter amount and a base direction.

**Splitting Prism:**
When total prism is split between two eyes, it is divided equally to reduce lens thickness.

**Horizontal Prism Splitting:**
• BO is split: half in each eye, both BO.
• BI is split: half in each eye, both BI.
• Example: 6Δ BO total → 3Δ BO OD, 3Δ BO OS.

**Vertical Prism Splitting:**
• If prescribed 4Δ BU OD → split as 2Δ BU OD and 2Δ BD OS.
• Vertical prism is split by putting the OPPOSITE base direction in the other eye.

**Verifying Prism with a Lensometer:**
• Place the lens on the lensometer and read the prism at the point that corresponds to the patient's PD.
• The distance between the target center and the lensometer's crosshair indicates the amount of prism.
• Each ring on the prism scale typically represents 1Δ.

**Imbalance at Near (for Multifocals):**
• When eyes have different vertical powers and the patient looks through the reading zone, unequal prismatic effects occur.
• This is called **vertical imbalance**.
• Corrected with: slab-off, reverse slab-off, dissimilar segs, or press-on prism.
• **Slab-off** is applied to the most PLUS (or least MINUS) lens.`,
      },
    ],
  },

  measurements: {
    title: 'Patient Measurements & Fittings',
    sections: [
      {
        title: 'Pupillary Distance (PD)',
        content: `**PD (Pupillary Distance)** — The distance between the centers of the pupils, measured in millimeters.

**Types of PD:**
• **Binocular PD** — Total distance from one pupil center to the other (e.g., 64mm).
• **Monocular PD** — Distance from the center of the bridge of the nose to each pupil center individually (e.g., OD 32 / OS 32).
  - Monocular PDs are more accurate because faces are rarely perfectly symmetrical.

**Distance PD vs. Near PD:**
• Distance PD — Measured while the patient looks at a distant target. Used for distance glasses.
• Near PD — Approximately **3mm less** than the distance PD (due to convergence of the eyes for near tasks).
  - For bifocals/progressives, the near PD is critical for proper seg placement.

**Measuring PD:**
1. Stand approximately 16 inches (40 cm) from the patient.
2. Hold a PD ruler against the patient's brow.
3. Close your right eye, align the zero mark with the patient's right pupil center.
4. Open your right eye, close your left eye, and read the measurement at the patient's left pupil.
5. For monocular PDs, measure from the center of the nose bridge to each pupil.

**Why PD Matters:**
• The optical center of the lens must align with the patient's pupil.
• Incorrect PD causes unwanted prismatic effect (per Prentice's Rule).
• Even 1-2mm of error can cause eye strain and discomfort, especially in higher Rx.`,
      },
      {
        title: 'Segment Height and Multifocal Measurements',
        content: `**Segment (Seg) Height:**
• The vertical distance from the lowest point of the lens (in the frame) to the top of the bifocal/trifocal segment or the fitting cross of a progressive lens.

**Measuring Seg Height:**
1. Fit the frame on the patient and adjust for proper alignment.
2. Using a marker or ruler, measure from the bottom of the lens opening to:
   - **Bifocal:** The lower eyelid margin (standard placement), typically at the lower pupil margin.
   - **Trifocal:** Typically at the lower pupil margin as well; the intermediate portion sits at pupil center.
   - **Progressive (PAL):** The center of the pupil (the fitting cross goes here).
3. Always measure with the frame properly adjusted on the patient's face.

**Standard Seg Heights:**
• Bifocal seg is typically placed at the lower lid margin or lower pupil edge.
• Progressive fitting cross is at pupil center.
• A seg set too high → patient uses the reading zone when looking at distance.
• A seg set too low → patient must tilt their head back excessively to read.

**Minimum Fitting Height:**
• Each progressive lens design has a minimum fitting height (typically 14-18mm).
• The frame's B measurement must accommodate this minimum.

**Seg Width / Type:**
• FT-28: Flat-top seg, 28mm wide — most common bifocal style.
• FT-35: Flat-top seg, 35mm wide — provides a wider near field.
• Round seg: Circular bifocal segment.
• Executive: Full-width segment extending across the entire lens.`,
      },
      {
        title: 'Vertex Distance and Other Measurements',
        content: `**Vertex Distance:**
• The distance from the back surface of the spectacle lens to the front of the cornea.
• Standard vertex distance: **12-14mm**.
• Must be compensated when converting between spectacle and contact lens prescriptions, especially for powers over **±4.00 D**.

**Vertex Distance Compensation Formula:**
Dc = D / (1 - d × D)
• Dc = compensated power
• D = original spectacle power
• d = change in vertex distance in METERS

**Example:** Spectacle Rx -8.00 at 14mm vertex distance. What is the contact lens power?
Dc = -8.00 / (1 - (0.014 × -8.00))
Dc = -8.00 / (1 + 0.112)
Dc = -8.00 / 1.112 = **-7.19 → -7.25 D (rounded)**

**Key Rule:**
• Moving a minus lens closer to the eye → less minus needed.
• Moving a plus lens closer to the eye → more plus needed.

**Pantoscopic Tilt Compensation:**
For every 2 degrees of pantoscopic tilt, add 1mm to the seg height of a progressive lens.

**Wrap Angle / Face Form:**
The curvature of the frame front. Excessive wrap can induce unwanted prismatic and power effects.

**Fitting Triangle / Frame Alignment:**
Ensure the frame is level, the vertex distance is equal for both eyes, and the PD is properly positioned.`,
      },
    ],
  },

  ansi: {
    title: 'ANSI Standards',
    sections: [
      {
        title: 'ANSI Z80.1 Overview',
        content: `**ANSI Z80.1** is the American National Standard for Prescription Ophthalmic Lenses. It establishes tolerances (acceptable limits of variation) for finished prescription lenses.

**Why ANSI Standards Matter:**
• They define the quality control standards for all prescription eyewear.
• Opticians must verify that finished lenses meet ANSI tolerances before dispensing.
• Non-compliant lenses should be rejected and remade.

**The standard covers tolerances for:**
• Sphere power
• Cylinder power
• Cylinder axis
• Add power
• Prismatic effect (prism)
• Prism imbalance between lenses
• Segment placement
• Impact resistance
• Lens surface quality

**Categories of Lenses Under ANSI:**
• **Single Vision**
• **Multifocal** (bifocal, trifocal, progressive)
• **Dress Eyewear** vs. **Safety Eyewear** (safety has tighter tolerances)

**Verification Process:**
Opticians use a **lensometer** (also called a focimeter or vertometer) to verify:
1. Sphere power
2. Cylinder power and axis
3. Prism amount and direction
4. Add power (for multifocals)
5. Optical center position`,
      },
      {
        title: 'Sphere, Cylinder, and Axis Tolerances',
        content: `**Single Vision Lens Tolerances (ANSI Z80.1):**

**Sphere Power Tolerance:**
| Sphere Power        | Tolerance |
|---------------------|-----------|
| 0.00 to ±6.50 D    | ±0.13 D   |
| Above ±6.50 D      | ±0.15 D   |

**Cylinder Power Tolerance:**
| Cylinder Power      | Tolerance |
|---------------------|-----------|
| 0.00 to 2.00 D     | ±0.13 D   |
| 2.12 to 4.50 D     | ±0.15 D   |
| Above 4.50 D        | 4% of cylinder power |

**Cylinder Axis Tolerance:**
| Cylinder Power      | Axis Tolerance |
|---------------------|---------------|
| 0.00 to 0.25 D     | ±14°          |
| 0.25 to 0.50 D     | ±7°           |
| 0.50 to 0.75 D     | ±5°           |
| 0.75 to 1.50 D     | ±3°           |
| Above 1.50 D       | ±2°           |

**Key Points:**
• Higher cylinder powers have tighter axis tolerances because even a small axis error causes more visual disturbance.
• Axis tolerance is most critical — a 5° axis error on a -2.00 cylinder creates more problem than a 0.13 D power error.
• Always verify axis accuracy carefully on the lensometer.`,
      },
      {
        title: 'Prism, Add Power, and Other Tolerances',
        content: `**Prism Tolerance (Unprescribed/Unwanted Prism):**

**Horizontal Prism (at the PRP — Prism Reference Point):**
| Sphere Power        | Tolerance      |
|---------------------|---------------|
| 0.00 to ±3.375 D   | ±0.33Δ per lens |
| Above ±3.375 D     | Determined by 1mm decentration |

**Vertical Prism:**
• Vertical imbalance between the two lenses: ±0.33Δ

**Prescribed Prism Tolerance:**
• ±0.33Δ for horizontal and vertical prism
• Base direction must match the prescription

**Add Power Tolerances (for Multifocals):**
| Add Power           | Tolerance |
|---------------------|-----------|
| Up to +4.00 D       | ±0.12 D   |
| Above +4.00 D       | ±0.18 D   |

**Segment Placement Tolerances:**
• Horizontal seg position: ±1.0mm from specified position
• Vertical seg position (seg height): ±1.0mm from specified position
• Seg tilt: ±2° from horizontal

**Impact Resistance:**
• All dress ophthalmic lenses must pass the FDA drop-ball test (a 5/8-inch steel ball dropped from 50 inches).
• Lenses must withstand impact without fracturing.
• This is a separate requirement from ANSI — it is an FDA regulation (21 CFR 801.410).

**Other Requirements:**
• Lenses should be free of visible defects (waves, bubbles, scratches) in the central 30mm zone.
• Cosmetic defects outside this zone may be acceptable.`,
      },
    ],
  },

  definitions: {
    title: 'Optical Terminology & Definitions',
    sections: [
      {
        title: 'Fundamental Optical Terms',
        content: `**Diopter (D)** — The unit of measurement for the refractive power of a lens. It is the reciprocal of the focal length in meters: D = 1/f.

**Index of Refraction** — A number that describes how much a material bends light. Higher index = more bending = thinner lenses. Air = 1.00; CR-39 = 1.50; Polycarbonate = 1.586.

**Focal Point** — The point where parallel light rays converge (plus lens) or appear to diverge from (minus lens) after passing through a lens.

**Focal Length** — The distance from the lens to its focal point. f = 1/D.

**Vergence** — The direction and amount of bending of light rays. Measured in diopters.
• Convergence (+ vergence) — light rays coming together.
• Divergence (- vergence) — light rays spreading apart.

**Refraction** — The bending of light as it passes from one medium to another.

**Snell's Law** — Describes the relationship between angles of incidence and refraction: n₁ sin θ₁ = n₂ sin θ₂.

**Total Internal Reflection** — When light strikes a boundary at an angle greater than the critical angle, it reflects entirely back into the material.

**Abbe Value (V-number)** — A measure of a material's dispersion (how much it separates white light into its component colors). Higher Abbe value = less chromatic aberration.

**Chromatic Aberration** — Color fringing around objects, caused by the lens dispersing light into its component wavelengths. More noticeable with low Abbe value materials.`,
      },
      {
        title: 'Lens and Frame Terminology',
        content: `**Optical Center (OC)** — The point on a lens through which light passes without any prismatic deviation.

**Major Reference Point (MRP) / Prism Reference Point (PRP)** — The point at which prism is measured; in lenses without prescribed prism, this coincides with the optical center.

**Geometric Center** — The physical center of the lens opening in the frame. May differ from the optical center.

**Decentration** — Moving the optical center of the lens from the geometric center of the frame to align with the patient's pupil. Decentration = (Frame PD - Patient PD) / 2.

**Effective Diameter (ED)** — The longest diameter of the lens shape, used to determine the minimum blank size needed.

**Minimum Blank Size (MBS)** — The smallest uncut lens blank that will fill the frame: MBS = ED + 2 × decentration.

**Surfacing** — The process of grinding a lens to the correct curvature and power.

**Edging** — The process of cutting a lens to the shape of the frame.

**Seg Drop** — The vertical distance from the geometric center of the lens to the top of the bifocal segment.

**Base Curve** — The curve on the front surface of the lens from which other curves are calculated.

**Back Vertex Power** — The power of the lens measured from the back surface; this is what the lensometer reads and what the Rx specifies.`,
      },
      {
        title: 'Clinical and Dispensing Terms',
        content: `**Accommodation** — The ability of the crystalline lens to change shape to focus at different distances. Decreases with age (leading to presbyopia).

**Amplitude of Accommodation** — The maximum amount of accommodation available, measured in diopters. Approximate formula: 18.5 - (0.3 × age).

**Convergence** — The inward turning of the eyes to maintain single binocular vision when viewing near objects.

**Anisometropia** — A significant difference in refractive error between the two eyes (typically >1.00 D difference).

**Aniseikonia** — A difference in the perceived image size between the two eyes.

**Amblyopia (Lazy Eye)** — Reduced vision in one eye due to abnormal visual development, not fully correctable with lenses.

**Strabismus** — A misalignment of the eyes. Types:
• Esotropia — eye turns inward
• Exotropia — eye turns outward
• Hypertropia — eye turns upward
• Hypotropia — eye turns downward

**Phoria** — A latent tendency for the eyes to deviate, corrected by the fusion mechanism.
• Esophoria — tendency to turn inward
• Exophoria — tendency to turn outward

**Diplopia** — Double vision. Can be monocular (one eye) or binocular (both eyes).

**Vertex Distance** — Distance from the back of the lens to the cornea (~12-14mm).

**Pantoscopic Tilt** — The angle at which the lower rim of the frame is closer to the face than the upper rim. Standard: 8-12°.

**Retroscopic Tilt** — The reverse of pantoscopic tilt; the upper rim is closer to the face.

**Pupillary Distance (PD)** — Distance between the centers of the pupils.`,
      },
      {
        title: 'Regulations and Professional Standards',
        content: `**ABO (American Board of Opticianry)** — The certifying body for opticians in the United States. The ABO certification demonstrates competency in dispensing ophthalmic eyewear.

**NCLE (National Contact Lens Examiners)** — Certifying body for contact lens fitting.

**FDA (Food and Drug Administration)** — Regulates impact resistance requirements for ophthalmic lenses. All lenses must pass the drop-ball test.

**Drop-Ball Test (21 CFR 801.410):**
• A 5/8-inch (15.88mm) steel ball is dropped from a height of 50 inches (127 cm).
• The lens must not fracture.
• Applies to all dress ophthalmic lenses in the United States.
• Raised center lenses must be tested individually (plus lenses where the center is the thinnest point after edging are exempt from batch testing).

**OSHA (Occupational Safety and Health Administration):**
• Sets workplace safety standards, including requirements for safety eyewear.
• Safety lenses must meet ANSI Z87.1 (not Z80.1) standards — these are stricter.
• Safety lenses must withstand high-velocity impact testing.
• Must be stamped with "Z87" on the lens and frame.

**ANSI Z80.1** — Standard for prescription dress eyewear (the primary standard for ABO).
**ANSI Z87.1** — Standard for safety eyewear (occupational and educational).

**FTC Eyeglass Rule (1978):** Requires prescribers to provide patients with a copy of their prescription immediately after an eye examination.

**Optician Scope of Practice:** Varies by state. Generally, opticians fit and dispense eyewear prescribed by ophthalmologists or optometrists. Opticians do NOT diagnose, treat, or prescribe.`,
      },
    ],
  },
};
